import { registrationTokenFor, rememberRegistrationToken } from '../utils/registrationSession';
import { rememberOnboardingToken } from '../utils/upestateReferral';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE_URL;
const UNAVAILABLE = 'Unexpected error! Please try again later!';
export const accountErrorMessage = (error) => {
  const body = error?.response?.data;
  return body?.error?.message || body?.message || error?.message || UNAVAILABLE;
};
export const hasAccountTokens = (data) =>
  ['accessToken', 'idToken', 'refreshToken'].every(
    (key) => typeof data?.[key] === 'string' && data[key].length > 0
  );
const accountFailure = (response) => {
  const message = response?.data?.error?.message || response?.data?.message || UNAVAILABLE;
  // Never retain Axios config/request: these can contain passwords and capabilities.
  return Object.assign(new Error(message), {
    status: response?.status || 0,
    response: { status: response?.status || 0, data: { success: false, error: { message } } }
  });
};
const accountResponse = async (request, { businessErrors = false } = {}) => {
  let response;
  try {
    response = await request();
  } catch (error) {
    throw accountFailure(error?.response);
  }
  const body = response?.data;
  if (businessErrors && response.status < 500 && body?.success === false && body.error?.message) {
    return body;
  }
  if (
    response.status >= 400 ||
    body?.statusCode >= 400 ||
    !body ||
    body.success === false ||
    body.error
  ) {
    throw accountFailure(response);
  }
  return body;
};

export const login = async (data) => {
  const body = await accountResponse(() => axios.post(`${API}/account/login`, data), {
    businessErrors: true
  });
  if (body.success === false || body.requiredPasswordReset || body.mfaChallengeName) return body;
  if (body.success !== true || !hasAccountTokens(body.data)) throw accountFailure();
  return body;
};

export const passwordRecover = async (data) => {
  const resp = await axios.post(`${API}/account/password/recover`, data);

  return resp.data;
};

export const passwordRecoverConfirm = async (data) => {
  const resp = await axios.post(`${API}/account/password/recover/confirm`, data);

  return resp.data;
};

export const changePassword = async (data) => {
  const resp = await axios.post(`${API}/account/password/change`, data);

  return resp.data;
};

export const resendRegisterConfirmationCode = async (email) => {
  const resp = await axios.post(`${API}/account/register/resend-code`, {
    email
  });

  return resp.data;
};

export const registerConfirm = async (data) => {
  const body = await accountResponse(() => axios.post(`${API}/account/register/confirm`, data), {
    businessErrors: true
  });
  if (typeof body.success !== 'boolean') throw accountFailure();
  return body;
};

export const createUser = async (data = {}) => {
  const body = await accountResponse(() =>
    axios.post(`${API}/account/register`, {
      ...data,
      ...(data.existingAccountData
        ? { registration_token: registrationTokenFor(data.existingAccountData.email) }
        : {})
    })
  );
  if (body.success !== true) throw accountFailure();
  rememberRegistrationToken(data.email, body.data?.registration_token);
  rememberOnboardingToken(data.email, body.data?.upestate_onboarding_token);
  return body;
};

export const getUser = async () => {
  const resp = await axios.get(`${API}/account/user`);

  return resp.data;
};

export const updateProfile = async (data) => {
  const resp = await axios.post(`${API}/account/user/profile`, data);

  return resp.data;
};

export const renewTokens = async (refreshToken) => {
  const resp = await axios.post(`${API}/account/token/renew`, {
    refreshToken
  });

  return resp.data;
};

export const deleteAccount = async () => {
  const resp = await axios.delete(`${API}/account/user`);

  return resp.data;
};
